import { Button, Icon, Popover } from "@screencloud/screencloud-ui-components";
import { MouseEvent, useState } from "react";
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { CompareContentItem } from "../../types";
import {
  CHANGE_ROW_TINT,
  PLAIN_ROW_HOVER_BACKGROUND,
} from "../../utils/changeStatus";
import { ChangeStatusLabel } from "../ChangeStatusLabel/ChangeStatusLabel";
import {
  AccentBar,
  Duration,
  FindButtonWrapper,
  HighlightOverlay,
  Position,
  Row,
  StatusSlot,
  Subtitle,
  Thumbnail,
  Title,
  TitleBlock,
} from "./styles";

export interface CompareRowProps {
  item: CompareContentItem;
  /** Column identity, keeps test ids unique when an item appears on both sides. */
  sideKey: string;
  /** Locates this item in the changes column (live rows only, GRWBKK-1767). */
  onFind?: () => void;
  /** Temporarily marks this row as the find target. */
  highlighted?: boolean;
  /** Reports pointer hover so the matching row can mirror it (live rows). */
  onHoverChange?: (hovering: boolean) => void;
  /** Mirrors the hover of the corresponding live row (changes rows). */
  linkedHover?: boolean;
}

const CompareRowBase = ({
  item,
  sideKey,
  onFind,
  highlighted,
  onHoverChange,
  linkedHover,
  intl,
}: CompareRowProps & InjectedIntlProps) => {
  const [findVisible, setFindVisible] = useState(false);
  const { changeType, movedFrom, movedTo } = item;
  const tint = changeType ? CHANGE_ROW_TINT[changeType] : null;
  const isRemoved = changeType === "removed";
  const rowTestId = `compare-${sideKey}-row-${item.id}`;
  const isEditedAndMoved =
    changeType === "edited" && movedFrom !== undefined && movedTo !== undefined;

  const statusLabel = changeType && (
    <ChangeStatusLabel changeType={changeType} idSuffix={item.id} />
  );

  const findButton = onFind && (
    <FindButtonWrapper $visible={findVisible}>
      <Button
        icon
        mini
        data-testid={`${rowTestId}-find`}
        aria-label={intl.formatMessage({
          id: "compare_changes.find",
          defaultMessage: "Find",
        })}
        onClick={(event: MouseEvent) => {
          // The whole row triggers the same action; don't fire it twice.
          event.stopPropagation();
          onFind();
        }}
        onFocus={() => setFindVisible(true)}
        onBlur={() => setFindVisible(false)}
      >
        <Icon name="search" />
      </Button>
    </FindButtonWrapper>
  );

  return (
    <Row
      data-testid={rowTestId}
      data-linked-hover={linkedHover || undefined}
      $background={tint?.background}
      $hoverBackground={tint?.hoverBackground ?? PLAIN_ROW_HOVER_BACKGROUND}
      $linkedHover={linkedHover}
      $clickable={Boolean(onFind)}
      onClick={onFind}
      onMouseEnter={() => {
        setFindVisible(true);
        onHoverChange?.(true);
      }}
      onMouseLeave={() => {
        setFindVisible(false);
        onHoverChange?.(false);
      }}
    >
      {tint && <AccentBar $color={tint.bar} />}
      {highlighted && (
        <HighlightOverlay data-testid={`${rowTestId}-highlight`} />
      )}
      <Position
        $highlighted={Boolean(changeType) && changeType !== "unchanged"}
      >
        {item.position}.
      </Position>
      <Thumbnail>
        {item.thumbnailUrl && <img src={item.thumbnailUrl} alt="" />}
      </Thumbnail>
      <TitleBlock>
        <Title data-testid={`${rowTestId}-title`} $muted={isRemoved}>
          {item.title}
        </Title>
        {item.subtitle && <Subtitle>{item.subtitle}</Subtitle>}
      </TitleBlock>
      {item.durationLabel && <Duration>{item.durationLabel}</Duration>}
      {findButton}
      <StatusSlot>
        {isEditedAndMoved ? (
          <Popover
            inverted
            content={
              <FormattedMessage
                id="compare_changes.tooltip.edited_and_moved"
                defaultMessage="Edited · Moved from position {from} to {to}"
                values={{ from: movedFrom, to: movedTo }}
              />
            }
            trigger={
              <span data-testid={`${rowTestId}-status`}>{statusLabel}</span>
            }
          />
        ) : (
          statusLabel
        )}
      </StatusSlot>
    </Row>
  );
};

export const CompareRow = injectIntl(CompareRowBase);
